// src/features/dossiers/components/steps/StepClient.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
    Box, Typography, Stack, TextField, InputAdornment,
    CircularProgress, Paper, Avatar, Divider, Button, alpha,
} from '@mui/material';
import {
    SearchOutlined, PersonAddOutlined, CheckCircle,
    EmailOutlined, PhoneOutlined, HomeOutlined, CloseOutlined,
} from '@mui/icons-material';
import apiClient from '../../../../services/api-client';

const FIELD_SX = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px', fontSize: '0.88rem',
        fontFamily: "'DM Sans', sans-serif",
        '& fieldset': { borderColor: 'rgba(26,26,46,0.12)' },
        '&:hover fieldset': { borderColor: 'rgba(201,168,76,0.4)' },
        '&.Mui-focused fieldset': { borderColor: '#c9a84c', borderWidth: '1.5px' },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: '#c9a84c' },
};

function FieldLabel({ children }) {
    return (
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#1a1a2e', letterSpacing: '0.06em', textTransform: 'uppercase', mb: 0.8 }}>
            {children}
        </Typography>
    );
}

export function StepClient({ data, onChange }) {
    const [mode, setMode] = useState(data.clientId ? 'selected' : 'search');
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [newClient, setNewClient] = useState({ nomComplet: '', email: '', telephone: '', adresseFacturation: '' });

    useEffect(() => {
        const searchClients = async () => {
            if (query.length < 3) {
                setResults([]);
                return;
            }
            setSearching(true);
            try {
                const res = await apiClient.get(`/api/clients/search?q=${query}`);
                setResults(res.data);
            } catch (err) {
                console.error(err);
            } finally {
                setSearching(false);
            }
        };

        const timer = setTimeout(searchClients, 500); // Attendre 500ms après la frappe
        return () => clearTimeout(timer);
    }, [query]);

    const selectClient = (client) => {
        onChange({ clientId: client.id, clientInfo: client });
        setMode('selected');
    };

    const clearClient = () => {
        onChange({ clientId: null, clientInfo: null });
        setMode('search');
        setQuery('');
    };

    const handleNewClientChange = (field, value) => {
        const updated = { ...newClient, [field]: value };
        setNewClient(updated);
        // On pré-remplit clientInfo pour la synthèse (le clientId sera assigné à la création)
        onChange({ clientId: '__new__', clientInfo: { ...updated, isNew: true } });
    };

    // ── Client sélectionné ────────────────────────────────────────────────────
    if (mode === 'selected') {
        const c = data.clientInfo;
        const initials = c?.nomComplet?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() ?? '??';
        return (
            <Box>
                <SectionTitle>Client du dossier</SectionTitle>
                <Box sx={{ p: 3, borderRadius: '16px', bgcolor: '#1a1a2e', position: 'relative', overflow: 'hidden' }}>
                    <Box sx={{ position: 'absolute', top: -30, right: -30, width: 130, height: 130, borderRadius: '50%', background: 'radial-gradient(circle, rgba(201,168,76,0.15) 0%, transparent 70%)' }} />
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Box sx={{ width: 48, height: 48, borderRadius: '13px', bgcolor: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <Typography sx={{ color: '#c9a84c', fontWeight: 800, fontSize: '0.95rem' }}>{initials}</Typography>
                            </Box>
                            <Box>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.3 }}>
                                    <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '1rem' }}>{c?.nomComplet}</Typography>
                                    {c?.isNew && <Box sx={{ px: 1, py: 0.2, borderRadius: '6px', bgcolor: 'rgba(201,168,76,0.2)', border: '1px solid rgba(201,168,76,0.3)' }}><Typography sx={{ color: '#c9a84c', fontSize: '0.6rem', fontWeight: 800, letterSpacing: '0.06em' }}>NOUVEAU</Typography></Box>}
                                </Stack>
                                <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.78rem' }}>{c?.email}</Typography>
                                {c?.telephone && <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.75rem' }}>{c.telephone}</Typography>}
                            </Box>
                        </Stack>
                        <Button onClick={clearClient} startIcon={<CloseOutlined sx={{ fontSize: '14px !important' }} />} size="small"
                            sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.72rem', fontFamily: "'DM Sans', sans-serif", textTransform: 'none', '&:hover': { color: '#ff6b6b', bgcolor: 'rgba(255,107,107,0.1)' } }}>
                            Changer
                        </Button>
                    </Stack>
                </Box>
            </Box>
        );
    }

    return (
        <Box>
            <SectionTitle>Client du dossier</SectionTitle>

            {/* Toggle search / nouveau */}
            <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
                {[{ key: 'search', label: 'Rechercher un client existant' }, { key: 'new', label: 'Créer un nouveau client' }].map(opt => (
                    <Button key={opt.key} onClick={() => setMode(opt.key)} size="small"
                        sx={{
                            borderRadius: '10px', px: 2, py: 0.8, fontSize: '0.78rem',
                            fontFamily: "'DM Sans', sans-serif", textTransform: 'none', fontWeight: 600,
                            bgcolor: mode === opt.key ? '#1a1a2e' : 'transparent',
                            color: mode === opt.key ? 'white' : 'rgba(26,26,46,0.5)',
                            border: '1px solid', borderColor: mode === opt.key ? '#1a1a2e' : 'rgba(26,26,46,0.12)',
                            '&:hover': { bgcolor: mode === opt.key ? '#1a1a2e' : 'rgba(26,26,46,0.04)' },
                        }}
                    >{opt.label}</Button>
                ))}
            </Stack>

            {/* Mode recherche */}
            {mode === 'search' && (
                <Box>
                    <TextField fullWidth placeholder="Nom, prénom ou email..." value={query} onChange={e => setQuery(e.target.value)}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">{searching ? <CircularProgress size={16} sx={{ color: '#c9a84c' }} /> : <SearchOutlined fontSize="small" sx={{ color: query ? '#c9a84c' : 'rgba(26,26,46,0.3)' }} />}</InputAdornment>,
                        }}
                        sx={{ ...FIELD_SX, mb: 1.5 }}
                    />
                    {results.length > 0 && (
                        <Stack spacing={1}>
                            {results.map(c => {
                                const init = c.nomComplet.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
                                return (
                                    <Box key={c.id} onClick={() => selectClient(c)}
                                        sx={{ p: 2, borderRadius: '12px', bgcolor: 'white', border: '1px solid rgba(26,26,46,0.07)', cursor: 'pointer', transition: 'all 0.2s', '&:hover': { borderColor: 'rgba(201,168,76,0.4)', boxShadow: '0 4px 16px rgba(26,26,46,0.07)', transform: 'translateY(-1px)' } }}
                                    >
                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                            <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: 'rgba(26,26,46,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                <Typography sx={{ fontWeight: 800, fontSize: '0.72rem', color: 'rgba(26,26,46,0.5)' }}>{init}</Typography>
                                            </Box>
                                            <Box sx={{ flexGrow: 1 }}>
                                                <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: '#1a1a2e' }}>{c.nomComplet}</Typography>
                                                <Typography sx={{ fontSize: '0.72rem', color: 'rgba(26,26,46,0.45)' }}>{c.email} · {c.telephone}</Typography>
                                            </Box>
                                            <CheckCircle sx={{ fontSize: 18, color: 'rgba(26,26,46,0.15)' }} />
                                        </Stack>
                                    </Box>
                                );
                            })}
                        </Stack>
                    )}
                    {query.length >= 2 && !searching && results.length === 0 && (
                        <Box sx={{ p: 2.5, borderRadius: '12px', bgcolor: 'rgba(26,26,46,0.03)', border: '1px dashed rgba(26,26,46,0.12)', textAlign: 'center' }}>
                            <Typography sx={{ fontSize: '0.82rem', color: 'rgba(26,26,46,0.45)' }}>Aucun résultat pour «&nbsp;{query}&nbsp;»</Typography>
                            <Button onClick={() => setMode('new')} size="small" startIcon={<PersonAddOutlined sx={{ fontSize: '14px !important' }} />}
                                sx={{ mt: 1, color: '#c9a84c', fontFamily: "'DM Sans', sans-serif", textTransform: 'none', fontSize: '0.78rem', fontWeight: 600 }}>
                                Créer ce client
                            </Button>
                        </Box>
                    )}
                </Box>
            )}

            {/* Mode nouveau client */}
            {mode === 'new' && (
                <Box sx={{ p: 3, borderRadius: '16px', bgcolor: 'rgba(26,26,46,0.02)', border: '1px solid rgba(26,26,46,0.08)' }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2.5 }}>
                        <Box sx={{ gridColumn: '1 / -1' }}>
                            <FieldLabel>Nom complet *</FieldLabel>
                            <TextField fullWidth placeholder="Jean Dupont" value={newClient.nomComplet} onChange={e => handleNewClientChange('nomComplet', e.target.value)} sx={FIELD_SX} />
                        </Box>
                        <Box>
                            <FieldLabel>Email *</FieldLabel>
                            <TextField fullWidth placeholder="jean@email.com" value={newClient.email} onChange={e => handleNewClientChange('email', e.target.value)}
                                InputProps={{ startAdornment: <InputAdornment position="start"><EmailOutlined sx={{ fontSize: 16, color: 'rgba(26,26,46,0.3)' }} /></InputAdornment> }}
                                sx={FIELD_SX} />
                        </Box>
                        <Box>
                            <FieldLabel>Téléphone</FieldLabel>
                            <TextField fullWidth placeholder="+225 07 00 00 00" value={newClient.telephone} onChange={e => handleNewClientChange('telephone', e.target.value)}
                                InputProps={{ startAdornment: <InputAdornment position="start"><PhoneOutlined sx={{ fontSize: 16, color: 'rgba(26,26,46,0.3)' }} /></InputAdornment> }}
                                sx={FIELD_SX} />
                        </Box>
                        <Box sx={{ gridColumn: '1 / -1' }}>
                            <FieldLabel>Adresse de facturation</FieldLabel>
                            <TextField fullWidth placeholder="Cocody, Abidjan, Côte d'Ivoire" value={newClient.adresseFacturation} onChange={e => handleNewClientChange('adresseFacturation', e.target.value)}
                                InputProps={{ startAdornment: <InputAdornment position="start"><HomeOutlined sx={{ fontSize: 16, color: 'rgba(26,26,46,0.3)' }} /></InputAdornment> }}
                                sx={FIELD_SX} />
                        </Box>
                    </Box>
                </Box>
            )}
        </Box>
    );
}

function SectionTitle({ children }) {
    return (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
            <Typography sx={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.35rem', fontWeight: 700, color: '#1a1a2e' }}>{children}</Typography>
            <Box sx={{ flexGrow: 1, height: 1, bgcolor: 'rgba(26,26,46,0.07)' }} />
        </Stack>
    );
}